import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, CalendarClock, Send } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../ui/dialog";
import { Trident } from "./Logo";
import { WHATSAPP_URL } from "./shared";
import { fmtMil } from "./MethodFlow";
import { fmtPct } from "./simulatorMath";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { nome: "", email: "", whatsapp: "", tipo_negocio: "", mensagem: "" };

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-tria-subtle outline-none transition-colors focus:border-tria-sky/60 focus:bg-white/[0.06]";

const buildSummary = (sim) =>
  `Simulação do método — custo do lote: ${fmtMil(sim.custo)} · receita projetada: ${fmtMil(sim.receitaProj)} · ` +
  `despesas variáveis: ${fmtPct(sim.despVar, 0)} · despesas fixas: ${fmtMil(sim.despesasFixas)} · ` +
  `receita realizada: ${fmtMil(sim.receitaReal)} · margem bruta: ${fmtPct(sim.margemBrutaPct)} · ` +
  `margem de contribuição: ${fmtPct(sim.imcPct)} · ` +
  `ponto de equilíbrio: ${sim.pe != null ? fmtMil(sim.pe) : "não calculável"} · ` +
  `resultado estimado: ${fmtMil(sim.resultado)}`;

const buildSimPayload = (sim) => ({
  custo_lote: sim.custo,
  receita_projetada: sim.receitaProj,
  despesas_variaveis_percentual: sim.despVar,
  despesas_fixas: sim.despesasFixas,
  receita_realizada: sim.receitaReal,
  margem_bruta_percentual: Number(sim.margemBrutaPct.toFixed(2)),
  imc_estimado: Number(sim.imcPct.toFixed(2)),
  ponto_equilibrio: sim.pe != null ? Number(sim.pe.toFixed(2)) : null,
  faturamento_adicional: sim.falta != null ? Number(sim.falta.toFixed(2)) : null,
  resultado_estimado: Number(sim.resultado.toFixed(2)),
  status: sim.status,
});

const simResultLine = (sim) => {
  if (sim.pe == null) return "cenário inviável";
  if (sim.status === "acima" || sim.status === "equilibrio") return `resultado ${fmtMil(sim.resultado)}`;
  return `faltam ${fmtMil(sim.falta)}`;
};

export const LeadDialog = ({ trigger, sim = null }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open && sim) {
      setForm((f) => ({ ...f, mensagem: buildSummary(sim) }));
    }
  }, [open, sim]);

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v && status === "success") {
      setTimeout(() => {
        setForm(EMPTY);
        setStatus("idle");
      }, 350);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        origem: sim ? "simulacao" : "landing",
        simulacao: sim ? buildSimPayload(sim) : null,
      });
      setStatus("success");
      toast.success(sim ? "Simulação recebida! O PDF chega no seu e-mail em instantes." : "Contato recebido! Nossa equipe vai falar com você.");
    } catch {
      setStatus("idle");
      toast.error("Não foi possível enviar. Tente novamente ou chame no WhatsApp.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="lead-dialog" className="border-white/10 bg-tria-navy text-white sm:max-w-md">
        {status === "success" ? (
          <div data-testid="lead-success" className="flex flex-col items-center gap-4 py-8 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-400/15">
              <CheckCircle2 className="h-7 w-7 text-emerald-400" />
            </span>
            <h3 className="font-display text-xl font-semibold text-white">
              {sim ? "Simulação a caminho!" : "Recebemos seus dados!"}
            </h3>
            <p className="max-w-xs text-[13.5px] leading-relaxed text-tria-mist">
              {sim
                ? "Você recebe o PDF da simulação no seu e-mail em instantes — e a equipe da Sapientia mostra como aplicar o método no seu negócio."
                : "A equipe da Sapientia Consultores entrará em contato para agendar a sua demonstração da TRIA."}
            </p>
            <a
              data-testid="lead-success-whatsapp"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 text-[13px] font-semibold text-tria-sky underline decoration-tria-sky/40 underline-offset-4 transition-colors hover:text-white"
            >
              Quer agilizar? Fale agora no WhatsApp
            </a>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="mb-1 flex items-center gap-2.5">
                <Trident id="lead-dialog" className="h-7 w-7" />
                <DialogTitle className="font-display text-xl font-semibold text-white">
                  {sim ? "Receber minha simulação" : "Agendar demonstração"}
                </DialogTitle>
              </div>
              <DialogDescription className="text-left text-[13px] leading-relaxed text-tria-mist">
                {sim
                  ? "Deixe seus dados e a equipe da Sapientia envia este cenário em PDF, com a leitura gerencial completa."
                  : "Deixe seus dados e a equipe da Sapientia retorna para agendar uma demonstração ao vivo da TRIA."}
              </DialogDescription>
            </DialogHeader>
            {sim && (
              <div data-testid="lead-sim-summary" className="mt-2 rounded-xl border border-tria-sky/25 bg-tria-sky/[0.06] px-4 py-3">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-tria-sky">Sua simulação</p>
                <p className="mt-1.5 font-mono text-[11px] leading-relaxed text-tria-mist">
                  Margem de contribuição {fmtPct(sim.imcPct)} · PE {sim.pe != null ? fmtMil(sim.pe) : "—"} · {simResultLine(sim)}
                </p>
              </div>
            )}
            <form onSubmit={onSubmit} className="mt-2 flex flex-col gap-3.5">
              <div>
                <label htmlFor="lead-nome" className="mb-1.5 block text-[12px] font-semibold text-tria-mist">Nome *</label>
                <input id="lead-nome" data-testid="lead-nome-input" required minLength={2} value={form.nome} onChange={set("nome")} placeholder="Seu nome" className={inputCls} />
              </div>
              <div>
                <label htmlFor="lead-email" className="mb-1.5 block text-[12px] font-semibold text-tria-mist">E-mail *</label>
                <input id="lead-email" data-testid="lead-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="voce@empresa.com.br" className={inputCls} />
              </div>
              <div>
                <label htmlFor="lead-whatsapp" className="mb-1.5 block text-[12px] font-semibold text-tria-mist">WhatsApp *</label>
                <input id="lead-whatsapp" data-testid="lead-whatsapp-input" type="tel" required minLength={8} value={form.whatsapp} onChange={set("whatsapp")} placeholder="(45) 99999-0000" className={inputCls} />
              </div>
              <div>
                <label htmlFor="lead-tipo" className="mb-1.5 block text-[12px] font-semibold text-tria-mist">Tipo de negócio *</label>
                <select id="lead-tipo" data-testid="lead-tipo-select" required value={form.tipo_negocio} onChange={set("tipo_negocio")} className={`${inputCls} appearance-none`}>
                  <option value="" disabled className="bg-tria-navy">Selecione…</option>
                  <option value="Brechó" className="bg-tria-navy">Brechó</option>
                  <option value="Varejo / loja de moda" className="bg-tria-navy">Varejo / loja de moda</option>
                  <option value="Operação com compra por lote" className="bg-tria-navy">Operação com compra por lote</option>
                  <option value="Outro" className="bg-tria-navy">Outro</option>
                </select>
              </div>
              <div>
                <label htmlFor="lead-mensagem" className="mb-1.5 block text-[12px] font-semibold text-tria-mist">
                  {sim ? "Resumo da simulação" : "Mensagem (opcional)"}
                </label>
                <textarea id="lead-mensagem" data-testid="lead-mensagem-input" rows={sim ? 4 : 2} value={form.mensagem} onChange={set("mensagem")} placeholder="Conte um pouco sobre a sua operação…" className={`${inputCls} resize-none ${sim ? "font-mono text-[11.5px]" : ""}`} />
              </div>
              <button
                data-testid="lead-submit-btn"
                type="submit"
                disabled={status === "loading"}
                className="glow-blue mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-tria-vivid px-6 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-tria-blue disabled:opacity-60"
              >
                {status === "loading" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : sim ? (
                  <Send className="h-4 w-4" />
                ) : (
                  <CalendarClock className="h-4 w-4" />
                )}
                {sim ? "Enviar e receber minha simulação" : "Quero agendar minha demonstração"}
              </button>
              <p className="text-center text-[11px] text-tria-subtle">Seus dados são usados apenas para o contato comercial.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
